<script lang="ts" setup>
defineProps<{ tabs: { title: string, url: string }[], activeTab: number | undefined }>()

const scrollerEl = ref<HTMLElement | null>(null)
const showFadeLeft = ref(false)
const showFadeRight = ref(false)

function onScroll() {
  const el = scrollerEl.value
  if (!el)
    return

  const scrollableAmount = el.scrollWidth - el.clientWidth
  const threshold = 16

  showFadeLeft.value = el.scrollLeft > threshold
  showFadeRight.value = el.scrollLeft < scrollableAmount - threshold
}

onMounted(() => {
  scrollerEl.value?.addEventListener('scroll', onScroll)
  onScroll()
})
onUnmounted(() => {
  scrollerEl.value?.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div
    class="tab-group-container"
    :class="{
      'tab-group-container--fade-left': showFadeLeft,
      'tab-group-container--fade-right': showFadeRight,
    }"
  >
    <div ref="scrollerEl" class="tab-group-scroller">
      <ul class="tab-group">
        <li
          v-for="(tab, idx) in tabs"
          :key="tab.title"
          class="tab-group__tab"
          :class="{ 'tab-group__tab--active': idx === activeTab }"
        >
          <NuxtLink :to="$url(tab.url)">
            {{ tab.title }}
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

.tab-group-container {
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    height: 100%;
    width: 20%;
    pointer-events: none;
    opacity: 0.9;
    transform: scaleX(0);
    transition: transform 0.15s;
    z-index: 1;
  }
  &::before {
    left: calc(-1 * var(--spacing-s));
    background: linear-gradient(0.25turn, white 50%, transparent);
    transform-origin: left;
  }
  &::after {
    right: calc(-1 * var(--spacing-s));
    background: linear-gradient(0.25turn, transparent, white 50%);
    transform-origin: right;
  }

  &--fade-left {
    &::before {
      transform: scaleX(1);
    }
  }
  &--fade-right {
    &::after {
      transform: scaleX(1);
    }
  }
}

.tab-group-scroller {
  width: fit-content;
  margin-inline: calc(-1 * var(--spacing-s));
  padding-inline: var(--spacing-s);
  max-width: calc(100vw - var(--spacing-s));
  overflow: scroll;

  @media (min-width: $phablet) {
    margin-inline: calc(-1 * var(--spacing-m));
    padding-inline: var(--spacing-m);
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.tab-group {
  width: max-content;
  background-color: var(--gray-lighter);
  display: flex;
  flex-direction: row;
  gap: var(--spacing-xs);
  border-radius: 2.8rem;
  padding: 0.4rem 1.6rem;

  @media (min-width: $phablet) {
    flex-direction: column;
    padding: 0px;
    background-color: unset;
  }

  &__tab {
    margin-inline: var(--spacing-s);
    padding-block: var(--spacing-xs);
    width: fit-content;

    @media (min-width: $phablet) {
      margin-inline: unset;
    }

    &--active {
      background-image: url('/icons/wavyLineBlue.svg');
      position: relative;
      background-repeat: repeat-x;
      background-position-y: 95%;
      background-position-x: 50%;
    }

    &:hover {
      background-image: url('/icons/wavyLine.svg');
      position: relative;
      background-repeat: repeat-x;
      background-position-y: 95%;
    }
  }
}
</style>
